import type {
  AhpChatStartRequest,
  AhpSpaceSelection,
  EstablishedAhpSession,
  FirstTurnHandoff,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import {ensureSessionMode} from '@github-ui/ahp-session-client/session-mode'
import type {AgentTasksAppPayload} from '@github-ui/agent-sessions/types/agent-tasks-app-payload'
import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {CurrentUserProvider} from '@github-ui/agent-sessions/contexts/CurrentUserContext'
import {SidebarAgentFilterProvider} from '@github-ui/agent-sessions/contexts/SidebarAgentFilterContext'
import {useInitializeAuthToken} from '@github-ui/agent-sessions/hooks/use-initialize-auth-token'
import {useSSOChangeDetection} from '@github-ui/agent-sessions/hooks/use-sso-change-detection'
import {
  bindUserStartedTurn,
  markUserStartedTurn,
  unmarkUserStartedTurn,
} from '@github-ui/agent-sessions/utils/artifacts/user-started-turns'
import {createOptimisticEnvironmentTask} from '@github-ui/agent-sessions/hooks/use-create-environment-task'
import {TaskChat} from '@github-ui/agent-sessions/components/task-view/TaskChat'
import {activationTraceIdFromError, recordActivationTraceId} from '@github-ui/agent-sessions/ahp/activation-trace'
import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import type {OptimisticTask} from '@github-ui/agent-sessions/types/task'
import {ChatAutoProvisionRoute} from '@github-ui/copilot-chat-app/auto-provision-route'
import {CustomInstructionsProvider} from '@github-ui/copilot-chat-app/custom-instructions/context'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {chatHistoryQueryKeyPrefix} from '@github-ui/copilot-chat-app/runtime/chat-history'
import {
  SUBMIT_ERROR_MESSAGE,
  type SubmissionFailure,
  userFacingSubmissionMessage,
} from '@github-ui/ahp-session-client/submission-error'
import type {MissionControlRuntimeInput} from '@github-ui/copilot-chat-app/runtime/use-mission-control'
import {useMissionControlRuntime} from '@github-ui/copilot-chat-app/runtime/use-mission-control'
import {announce} from '@github-ui/aria-live'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {dynamic} from '@github-ui/dynamic'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {RootAppRouteErrorElement} from '@github-ui/react-core/future/RouterErrorBoundary'
import {useReportRouteError} from '@github-ui/react-core/future/use-report-route-error'
import {skipToken, useQuery, useQueryClient} from '@github-ui/react-query'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import {Outlet, type Location, useLocation, useNavigate, useRouteError} from '@github-ui/react-router'
import {
  createContext,
  Suspense,
  use,
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'

import {agentTasksQueryKey} from '@github-ui/agent-sessions/hooks/useTasks'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {getChatDraftStorageKeySuffix} from '@github-ui/one-copilot-composer/shared'
import type {NewSessionInitialInput} from '@github-ui/one-copilot-composer/new'
import {
  COPILOT_DEVELOPER_INTEGRATION_ID,
  DOTCOM_CHAT_AGENT_SLUG,
  getAgentsPath,
} from '@github-ui/agent-sessions/utils/constants'
import {taskNavigationState} from '@github-ui/agent-sessions/utils/task-helpers'
import {AgentsSidebarLayout} from './AgentsSidebarLayout'
import {AgentsWorkbenchLoadingState} from './AgentsWorkbenchLoadingState'
import {copilotAgentsTaskRoute} from './copilot-agents-task-route'
import {isQuickChatTaskViewEnabled, provisionalQuickChatTitle} from '../utils/is-quick-chat-task'
import {fetchCopilotChatPayload, type ChatPayload} from './copilot-chat-payload-route-query'
import styles from './CopilotAgentsPage.module.css'
import {type WorkbenchEnvironmentKind, provisioningWorkbenchOwner} from './agents-workbench-eligibility'

const DynamicAgentsWorkbenchHost = dynamic(async () => {
  const module = await import('@github-ui/agent-sessions/components/workbench/AgentsWorkbenchHost')
  return {default: module.AgentsWorkbenchHost}
})

interface StartLiveAgentsChatInput {
  /** Prompt, submission metadata, and locked Space the chat is provisioned from. */
  startRequest: AhpChatStartRequest
  config?: MissionControlRuntimeInput
  payload: AgentTasksAppPayload
  /**
   * Carried beside the start request because the agent mode is a destination
   * concern rather than part of the provisioning contract.
   */
  initialSelectedMode?: AgentMode
  externallyProvisioned?: boolean
  environmentKind?: WorkbenchEnvironmentKind
  /**
   * Set when a repository submission is shown in the provisional task view
   * before the task exists, so the view and failure recovery stay repository-shaped.
   */
  repository?: {nameWithOwner: string; branch?: string}
}

interface LiveAgentsChatState extends StartLiveAgentsChatInput {
  instanceKey: string
  entryKey: string
}

type RuntimeLiveAgentsChatState = LiveAgentsChatState & {config: MissionControlRuntimeInput}

function isRuntimeProvisioned(chat: LiveAgentsChatState): chat is RuntimeLiveAgentsChatState {
  return !chat.externallyProvisioned && chat.config !== undefined
}

function optimisticQuickChatTask(
  state: LiveAgentsChatState,
): HydratedAPITask & Pick<OptimisticTask, 'isOptimistic' | 'clientGeneratedId'> {
  const optimistic = createOptimisticEnvironmentTask({
    clientGeneratedId: state.instanceKey,
    problemStatement: state.startRequest.prompt.text,
  })
  const user = state.payload.currentUser
  const taskCreator = {id: user?.id ?? 0, login: user?.login}

  return {
    // These sentinels keep task actions disabled until the routed payload supplies real backend identifiers.
    id: '',
    isOptimistic: optimistic.isOptimistic,
    clientGeneratedId: optimistic.clientGeneratedId,
    creator: taskCreator,
    owner: {id: -1},
    repository: {id: -1},
    status: 'in_progress',
    state: 'in_progress',
    session_count: optimistic.session_count,
    created_at: optimistic.created_at,
    updated_at: optimistic.latest_session_updated_at,
    sessions: [],
    name: provisionalQuickChatTitle(state.startRequest.prompt.text),
    agent_collaborators: [
      {
        agent_id: 0,
        agent_task_id: '',
        agent_type: 'Integration',
        slug: provisionalAgentSlug(state),
      },
    ],
  }
}

function provisionalAgentSlug(state: Pick<LiveAgentsChatState, 'repository'>): string {
  return state.repository ? COPILOT_DEVELOPER_INTEGRATION_ID : DOTCOM_CHAT_AGENT_SLUG
}

/** What a pre-task failure failed to create, so its copy names a task rather than a chat for repository launches. */
export type LaunchTarget = 'chat' | 'task'

export interface LaunchSubmissionFailure extends SubmissionFailure {
  clientGeneratedId?: string
  launchTarget: LaunchTarget
}

function launchTarget(chat: Pick<StartLiveAgentsChatInput, 'repository'>): LaunchTarget {
  return chat.repository ? 'task' : 'chat'
}

export function launchCreationFailedTitle(target: LaunchTarget): string {
  return target === 'task' ? 'Task could not be created' : 'Chat could not be created'
}

type AgentsChatHandoff =
  | {phase: 'idle'}
  | {phase: 'legacy'; chat: LiveAgentsChatState; taskId?: string}
  | {phase: 'provisioning'; chat: LiveAgentsChatState}
  | {
      phase: 'routed'
      chat: LiveAgentsChatState
      taskId: string
      establishedSession?: EstablishedAhpSession
      establishedSessionPending: boolean
    }
  | {
      phase: 'failedBeforeTask'
      failure: LaunchSubmissionFailure
      recoveryInput: NewSessionInitialInput
      entryKey: string
      instanceKey: string
    }
  | {
      phase: 'failedAfterTask'
      chat: LiveAgentsChatState
      taskId: string
      establishedSession?: EstablishedAhpSession
      failure: SubmissionFailure
      failureDismissed?: boolean
      promptAccepted?: boolean
    }

const recoveryDraftKey = getChatDraftStorageKeySuffix('agents_page')

function persistRecoveryPrompt(prompt: string) {
  copilotLocalStorage.setSavedUserMessageOnError(recoveryDraftKey, prompt)
}

function releaseRecoveryPrompt(prompt: string, instanceKey?: string, recoveryInstanceKey?: string) {
  if (
    (instanceKey === undefined || instanceKey === recoveryInstanceKey) &&
    copilotLocalStorage.getSavedUserMessageOnError(recoveryDraftKey) === prompt
  ) {
    copilotLocalStorage.clearSavedUserMessageOnError(recoveryDraftKey)
  }
}

function provisioningErrorMetadata(error: unknown): {error_name: string; status_code?: number} {
  if (typeof error !== 'object' || error === null) return {error_name: 'UnknownError'}

  const errorRecord = error as Record<string, unknown>
  const constructorName = typeof errorRecord.constructor === 'function' ? errorRecord.constructor.name : undefined
  const errorName =
    error instanceof Error && error.name
      ? error.name
      : typeof errorRecord.name === 'string' && errorRecord.name
        ? errorRecord.name
        : constructorName || 'UnknownError'
  const response = errorRecord.response
  const responseStatus =
    typeof response === 'object' && response !== null ? (response as Record<string, unknown>).status : undefined
  const statusCode = errorRecord.statusCode ?? errorRecord.status ?? responseStatus

  return {
    error_name: errorName,
    ...(typeof statusCode === 'number' ? {status_code: statusCode} : {}),
  }
}

function unconsumedRecoveryPrompt(handoff: AgentsChatHandoff): string | undefined {
  if (handoff.phase === 'provisioning') return handoff.chat.startRequest.prompt.text
  if (handoff.phase === 'routed' && handoff.establishedSessionPending) return handoff.chat.startRequest.prompt.text
  if (handoff.phase === 'failedBeforeTask') return handoff.failure.text
  if (handoff.phase === 'failedAfterTask' && !handoff.promptAccepted) return handoff.failure.text
  return undefined
}

function recoveryPromptRequiringPersistence(handoff: AgentsChatHandoff): string | undefined {
  return handoff.phase === 'provisioning' || (handoff.phase === 'routed' && handoff.establishedSessionPending)
    ? handoff.chat.startRequest.prompt.text
    : undefined
}

function failedBeforeTaskRecoveryInput(chat: LiveAgentsChatState): NewSessionInitialInput {
  if (chat.repository) {
    const {nameWithOwner, branch} = chat.repository
    return {
      target: {kind: 'task', repository: {kind: 'name', nameWithOwner, ...(branch ? {branch} : {})}},
      // The repository handoff only accepts managed-Sandbox submissions.
      initialEnvironment: 'sandbox',
      behavior: 'prefill',
      prompt: chat.startRequest.prompt,
    }
  }
  const provisioningTarget = chat.startRequest.provisioningTarget
  const initialSelectedSpace = chat.startRequest.initialSelectedSpace
  return {
    target: {
      kind: 'chat',
      ...(provisioningTarget ? {provisioningTarget} : {}),
      ...(initialSelectedSpace ? {context: {kind: 'space', space: initialSelectedSpace}} : {}),
    },
    initialEnvironment: 'sandbox',
    behavior: 'prefill',
    prompt: chat.startRequest.prompt,
  }
}

export interface PersistentAgentsChatActions {
  startLiveChat: (input: StartLiveAgentsChatInput) => string
  resolveLiveChatTask: (instanceKey: string, taskId: string) => boolean
  failLiveChat: (instanceKey: string, errorMessage: string) => boolean
  adoptExternalSession: (instanceKey: string, session: EstablishedAhpSession) => void
  resetLiveChat: () => void
  submissionFailure: LaunchSubmissionFailure | undefined
  recoveryInput: NewSessionInitialInput | undefined
  recoveryPrompt: string | undefined
  recoveryInstanceKey: string | undefined
  settlement: {instanceKey: string; prompt: string} | undefined
  clearSubmissionFailure: () => void
  disableSandboxSubmission: boolean
  chatProviderError: Error | undefined
}

export interface PersistentAgentsChatSession {
  instanceKey?: string
  chatApiURL?: string
  launchEnvironmentKind?: WorkbenchEnvironmentKind
  launchOwnerLogin?: string
  externallyProvisioned?: boolean
  establishedSession?: EstablishedAhpSession
  establishedSessionPending: boolean
  provisioningTaskId?: string
  provisioningPrompt?: PreparedPrompt
  initialSelectedSpace?: AhpSpaceSelection
  provisioningDraft?: string
  onProvisioningDraftChange?: (draft: string) => void
  onProvisionedUserRename?: () => void
  submissionFailure?: SubmissionFailure
  restoredPrompt?: string
  retryPrompt?: PreparedPrompt
  configureRetrySession?: FirstTurnHandoff['configureSession']
  sessionBindingFailed?: boolean
  onRestoredPromptChange?: (prompt: string) => void
  onRestoredPromptAccepted?: (prompt: string) => void
  onRetrySessionEstablished?: (session: EstablishedAhpSession, prompt: string) => void
  clearSubmissionFailure: () => void
}

// Agent mode and tool approval are independent host RPCs. Under the independent-controls
// flag the approval mode is owned by its own picker, so deriving it from the agent mode here
// would silently overwrite the user's choice. A retry applies these the same way a kickoff does.
function configureSessionForMode(mode: AgentMode | undefined): FirstTurnHandoff['configureSession'] | undefined {
  if (!mode) return undefined
  const coupleApprovalToAgentMode = !isFeatureEnabled('copilot_mission_control_independent_mode_controls')
  return async establishedSession => {
    await Promise.all([
      // The kickoff carries `mode` on createSession, so this only writes when the host disagrees.
      ensureSessionMode(establishedSession, mode),
      ...(coupleApprovalToAgentMode ? [establishedSession.commands.setApproveAll(mode === 'autopilot')] : []),
    ])
  }
}

// Exported so pages can be rendered with a provided host value instead of the full host runtime.
export const PersistentAgentsChatContext = createContext<PersistentAgentsChatActions | undefined>(undefined)
const PersistentAgentsLiveChatContext = createContext(false)
export const PersistentAgentsChatSessionContext = createContext<PersistentAgentsChatSession | undefined>(undefined)

export function usePersistentAgentsChat() {
  const context = use(PersistentAgentsChatContext)
  if (!context) {
    throw new Error('usePersistentAgentsChat must be used within a PersistentAgentsChatHost')
  }
  return context
}

export function useOptionalPersistentAgentsChat() {
  return use(PersistentAgentsChatContext)
}

export function usePersistentAgentsChatSession() {
  return use(PersistentAgentsChatSessionContext)
}

function isOwnedLiveChatLocation(handoff: AgentsChatHandoff, location: Location) {
  if (handoff.phase === 'idle') return false
  if (handoff.phase === 'failedBeforeTask') return handoff.entryKey === location.key
  if (handoff.chat.entryKey === location.key) return true
  return (
    (handoff.phase === 'legacy' || handoff.phase === 'routed' || handoff.phase === 'failedAfterTask') &&
    handoff.taskId !== undefined &&
    location.pathname === generateTaskPathForRepo({taskId: handoff.taskId})
  )
}

export function PersistentAgentsChatHost() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isTaskRoute = location.pathname.startsWith('/copilot/tasks/')
  // This viewer-specific payload includes instructions and permission state, so it stays in memory rather than
  // persisting to IndexedDB. The legacy /github-copilot/chat endpoint also has no generated runtime schema.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema
  const chatProviderQuery = useQuery({
    queryKey: ['copilot-agents', 'chat-payload'],
    queryFn: fetchCopilotChatPayload,
    enabled: !isTaskRoute,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
  const {data: chatProviderPayload, error: chatProviderError} = chatProviderQuery
  const [publishedChatPayload, setPublishedChatPayload] = useState<ChatPayload>()
  const handleInstructionRulesPublished = useCallback(() => {
    if (chatProviderPayload) setPublishedChatPayload(chatProviderPayload)
  }, [chatProviderPayload])
  const instructionsReadyForChatPayload =
    chatProviderPayload !== undefined && publishedChatPayload === chatProviderPayload && !chatProviderError
  const disableSandboxSubmission = !isTaskRoute && !instructionsReadyForChatPayload
  const handleInstructionsProviderMounted = useCallback(() => setPublishedChatPayload(undefined), [])
  useEffect(() => {
    if (!isTaskRoute && chatProviderError) {
      announce(`Unable to load Copilot Chat. ${chatProviderError.message}`, {assertive: true})
    }
  }, [chatProviderError, isTaskRoute])
  // The live host replaces the task outlet before its route boundary can render, so observe the
  // cached task query here to report loader errors without unmounting the persistent chat.
  const {error: taskRouteError} = useQuery({
    queryKey: copilotAgentsTaskRoute.queries.mainQuery.makeQueryKey({pathname: location.pathname}),
    queryFn: skipToken,
    enabled: isTaskRoute,
  })
  const quickChatUsesTaskView = isQuickChatTaskViewEnabled()
  const [handoff, setHandoff] = useState<AgentsChatHandoff>({phase: 'idle'})
  const [provisioningDraft, setProvisioningDraft] = useState('')
  const [settlement, setSettlement] = useState<{instanceKey: string; prompt: string}>()
  const instanceCountRef = useRef(0)
  const activeInstanceKeyRef = useRef<string | undefined>(undefined)
  const recoveryInstanceKeyRef = useRef<string | undefined>(undefined)
  const externalProvisioningPromptsRef = useRef(new Map<string, {prompt: string; launchTarget: LaunchTarget}>())
  const startLiveChat = useCallback(
    (input: StartLiveAgentsChatInput) => {
      setSettlement(undefined)
      setProvisioningDraft('')
      instanceCountRef.current += 1
      const instanceKey = `agents-chat-${instanceCountRef.current}`
      activeInstanceKeyRef.current = instanceKey
      if (input.externallyProvisioned) {
        if (copilotLocalStorage.getSavedUserMessageOnError(recoveryDraftKey) === input.startRequest.prompt.text) {
          recoveryInstanceKeyRef.current = instanceKey
        }
        externalProvisioningPromptsRef.current.set(instanceKey, {
          prompt: input.startRequest.prompt.text,
          launchTarget: launchTarget(input),
        })
      } else {
        recoveryInstanceKeyRef.current = instanceKey
      }
      const chat = {
        ...input,
        instanceKey,
        entryKey: location.key,
      }
      setHandoff(quickChatUsesTaskView ? {phase: 'provisioning', chat} : {phase: 'legacy', chat})
      return instanceKey
    },
    [location.key, quickChatUsesTaskView],
  )
  const resolveLiveChatTask = useCallback(
    (instanceKey: string, taskId: string) => {
      const prompt = externalProvisioningPromptsRef.current.get(instanceKey)?.prompt
      if (prompt === undefined || !externalProvisioningPromptsRef.current.delete(instanceKey)) return false
      releaseRecoveryPrompt(prompt, instanceKey, recoveryInstanceKeyRef.current)
      setSettlement({instanceKey, prompt})
      void queryClient.invalidateQueries({queryKey: agentTasksQueryKey})
      if (activeInstanceKeyRef.current !== instanceKey) return false
      setHandoff(current =>
        current.phase === 'provisioning' &&
        current.chat.externallyProvisioned &&
        current.chat.instanceKey === instanceKey
          ? {phase: 'routed', chat: current.chat, taskId, establishedSessionPending: false}
          : current,
      )
      return true
    },
    [queryClient],
  )
  const failLiveChat = useCallback((instanceKey: string, errorMessage: string) => {
    const externalLaunch = externalProvisioningPromptsRef.current.get(instanceKey)
    if (!externalLaunch || !externalProvisioningPromptsRef.current.delete(instanceKey)) return false
    if (activeInstanceKeyRef.current !== instanceKey) return false
    activeInstanceKeyRef.current = undefined
    setProvisioningDraft('')
    announce(`${launchCreationFailedTitle(externalLaunch.launchTarget)}. ${errorMessage}`, {assertive: true})
    setHandoff(current =>
      current.phase === 'provisioning' && current.chat.externallyProvisioned && current.chat.instanceKey === instanceKey
        ? {
            phase: 'failedBeforeTask',
            failure: {
              clientGeneratedId: instanceKey,
              message: errorMessage,
              text: current.chat.startRequest.prompt.text,
              launchTarget: launchTarget(current.chat),
            },
            recoveryInput: failedBeforeTaskRecoveryInput(current.chat),
            entryKey: current.chat.entryKey,
            instanceKey,
          }
        : current,
    )
    return true
  }, [])
  const adoptExternalSession = useCallback((instanceKey: string, session: EstablishedAhpSession) => {
    if (activeInstanceKeyRef.current !== instanceKey) return
    setHandoff(current =>
      current.phase === 'routed' &&
      current.chat.externallyProvisioned &&
      current.chat.instanceKey === instanceKey &&
      current.establishedSession !== session
        ? {...current, establishedSession: session, establishedSessionPending: false}
        : current,
    )
  }, [])
  const resetLiveChat = useCallback(() => {
    activeInstanceKeyRef.current = undefined
    const recoveryPrompt = recoveryPromptRequiringPersistence(handoff)
    if (recoveryPrompt) {
      persistRecoveryPrompt(recoveryPrompt)
      if ('chat' in handoff) recoveryInstanceKeyRef.current = handoff.chat.instanceKey
    }
    setHandoff({phase: 'idle'})
  }, [handoff])
  const clearFailure = useCallback(
    () => setHandoff(current => (current.phase === 'failedBeforeTask' ? {phase: 'idle'} : current)),
    [],
  )
  const clearTaskSubmissionFailure = useCallback(
    () => setHandoff(current => (current.phase === 'failedAfterTask' ? {...current, failureDismissed: true} : current)),
    [],
  )
  const handleSessionProvisioned = useCallback(
    ({taskId}: {taskId?: string}) => {
      if (!taskId || handoff.phase !== 'legacy' || handoff.taskId || !isRuntimeProvisioned(handoff.chat)) return
      setHandoff({...handoff, taskId})
      void queryClient.invalidateQueries({queryKey: chatHistoryQueryKeyPrefix(handoff.chat.config.apiURL)})
      navigate(generateTaskPathForRepo({taskId}), {
        replace: true,
        preventAutofocus: true,
        state: taskNavigationState(handoff.chat.startRequest.prompt.text),
      })
    },
    [handoff, navigate, queryClient],
  )
  const handleTaskViewSessionProvisioned = useCallback((instanceKey: string, taskId: string, prompt: string) => {
    if (activeInstanceKeyRef.current !== instanceKey) return
    persistRecoveryPrompt(prompt)
    recoveryInstanceKeyRef.current = instanceKey
    setHandoff(current =>
      current.phase === 'provisioning' && current.chat.instanceKey === instanceKey
        ? {phase: 'routed', chat: current.chat, taskId, establishedSessionPending: true}
        : current,
    )
  }, [])
  const handleTaskViewSessionEstablished = useCallback(
    (instanceKey: string, session: EstablishedAhpSession, prompt: string) => {
      if (activeInstanceKeyRef.current !== instanceKey) return
      releaseRecoveryPrompt(prompt, instanceKey, recoveryInstanceKeyRef.current)
      setProvisioningDraft('')
      setHandoff(current => {
        if (
          (current.phase !== 'provisioning' && current.phase !== 'routed') ||
          current.chat.instanceKey !== instanceKey
        ) {
          return current
        }
        if (current.phase === 'routed') {
          return {...current, establishedSession: session, establishedSessionPending: false}
        }
        // Routed already has a task id; only the promotion depends on identity carrying one.
        const taskId = session.identity.taskId
        if (!taskId) return current
        return {
          phase: 'routed',
          chat: current.chat,
          taskId,
          establishedSession: session,
          establishedSessionPending: false,
        }
      })
      announce('Connected.')
    },
    [],
  )
  const handleTaskViewSessionEstablishmentFailed = useCallback(
    (instanceKey: string, failure: SubmissionFailure, provisionedTaskId: string | undefined) => {
      if (activeInstanceKeyRef.current !== instanceKey) return
      activeInstanceKeyRef.current = undefined
      persistRecoveryPrompt(failure.text)
      setHandoff(current => {
        if (!provisionedTaskId && current.phase === 'provisioning' && current.chat.instanceKey === instanceKey) {
          return {
            phase: 'failedBeforeTask',
            failure: {...failure, launchTarget: launchTarget(current.chat), clientGeneratedId: instanceKey},
            recoveryInput: failedBeforeTaskRecoveryInput(current.chat),
            entryKey: current.chat.entryKey,
            instanceKey,
          }
        }
        if (
          provisionedTaskId &&
          (current.phase === 'provisioning' || current.phase === 'routed') &&
          current.chat.instanceKey === instanceKey
        ) {
          return {
            phase: 'failedAfterTask',
            chat: current.chat,
            taskId: provisionedTaskId,
            establishedSession: current.phase === 'routed' ? current.establishedSession : undefined,
            failure,
            promptAccepted: false,
          }
        }
        return current
      })
    },
    [],
  )
  const routedTaskId = handoff.phase === 'routed' || handoff.phase === 'failedAfterTask' ? handoff.taskId : undefined
  const routedChatApiURL =
    handoff.phase === 'routed' || handoff.phase === 'failedAfterTask' ? handoff.chat.config?.apiURL : undefined
  const routedPrompt =
    handoff.phase === 'routed' || handoff.phase === 'failedAfterTask'
      ? handoff.chat.startRequest.prompt.text
      : undefined
  const handleAbandonedSubmissionSettled = useCallback(
    (apiURL: string, instanceKey: string, settledPrompt: string | undefined) => {
      // An abandoned submission that still succeeded owns a task, so its
      // recovery entry has to go; restoring it would invite a duplicate send.
      // A failed one stays recoverable. releaseRecoveryPrompt only clears an
      // entry still holding this exact text, so a newer attempt survives.
      if (settledPrompt !== undefined) {
        releaseRecoveryPrompt(settledPrompt, instanceKey, recoveryInstanceKeyRef.current)
        setSettlement({instanceKey, prompt: settledPrompt})
      }
      void queryClient.invalidateQueries({queryKey: chatHistoryQueryKeyPrefix(apiURL)})
      void queryClient.invalidateQueries({queryKey: agentTasksQueryKey})
    },
    [queryClient],
  )

  useEffect(() => {
    if (!quickChatUsesTaskView || !routedTaskId) return
    if (routedChatApiURL) void queryClient.invalidateQueries({queryKey: chatHistoryQueryKeyPrefix(routedChatApiURL)})
    // The flagged sidebar reads Mission Control's task list, not the chat
    // history list, and managed-sandbox provisioning POSTs the task directly
    // rather than going through the optimistic create path.
    void queryClient.invalidateQueries({queryKey: agentTasksQueryKey})
    navigate(generateTaskPathForRepo({taskId: routedTaskId}), {
      replace: true,
      state: routedPrompt ? taskNavigationState(routedPrompt) : undefined,
    })
  }, [navigate, queryClient, quickChatUsesTaskView, routedChatApiURL, routedPrompt, routedTaskId])
  const handoffIsOwned = handoff.phase === 'idle' || isOwnedLiveChatLocation(handoff, location)
  const recoverable = unconsumedRecoveryPrompt(handoff)
  const recoveryInstanceKey =
    handoff.phase === 'failedBeforeTask'
      ? handoff.instanceKey
      : handoff.phase === 'idle' || handoff.phase === 'legacy'
        ? undefined
        : handoff.chat.instanceKey
  const actions = useMemo(
    () => ({
      startLiveChat,
      resolveLiveChatTask,
      failLiveChat,
      adoptExternalSession,
      resetLiveChat,
      submissionFailure:
        handoff.phase === 'failedBeforeTask' && handoff.entryKey === location.key ? handoff.failure : undefined,
      recoveryInput: handoff.phase === 'failedBeforeTask' ? handoff.recoveryInput : undefined,
      recoveryPrompt:
        handoffIsOwned || (settlement?.instanceKey === recoveryInstanceKey && settlement?.prompt === recoverable)
          ? undefined
          : recoverable,
      recoveryInstanceKey,
      settlement,
      clearSubmissionFailure: clearFailure,
      disableSandboxSubmission,
      chatProviderError: chatProviderError ?? undefined,
    }),
    [
      clearFailure,
      chatProviderError,
      disableSandboxSubmission,
      adoptExternalSession,
      failLiveChat,
      handoff,
      handoffIsOwned,
      location.key,
      recoverable,
      recoveryInstanceKey,
      resolveLiveChatTask,
      resetLiveChat,
      settlement,
      startLiveChat,
    ],
  )

  const currentHandoff = useMemo<AgentsChatHandoff>(
    () => (handoffIsOwned ? handoff : {phase: 'idle'}),
    [handoff, handoffIsOwned],
  )
  useEffect(() => {
    if (handoffIsOwned) return
    activeInstanceKeyRef.current = undefined
    const recoveryPrompt = recoveryPromptRequiringPersistence(handoff)
    if (recoveryPrompt) {
      persistRecoveryPrompt(recoveryPrompt)
      if ('chat' in handoff) recoveryInstanceKeyRef.current = handoff.chat.instanceKey
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Route ownership is an external state boundary.
    setHandoff({phase: 'idle'})
  }, [handoff, handoffIsOwned])
  const provisioningTaskId =
    currentHandoff.phase === 'routed' || currentHandoff.phase === 'failedAfterTask' ? currentHandoff.taskId : undefined
  const provisionedUserRenameRef = useRef<(() => void) | undefined>(undefined)
  const focusedProvisionedTaskRef = useRef<string | undefined>(undefined)
  const provisionedFallbackFocusRef = useRef<HTMLElement | undefined>(undefined)
  const handleProvisionedUserRename = useCallback(() => provisionedUserRenameRef.current?.(), [])
  const handleRestoredPromptAccepted = useCallback((prompt: string) => {
    setProvisioningDraft('')
    releaseRecoveryPrompt(prompt, undefined, recoveryInstanceKeyRef.current)
    setHandoff(current => {
      if (current.phase === 'failedAfterTask' && current.failure.text === prompt) {
        return {...current, promptAccepted: true}
      }
      return current
    })
  }, [])
  const handleRestoredPromptChange = useCallback((prompt: string) => {
    persistRecoveryPrompt(prompt)
    setHandoff(current => {
      if (current.phase === 'failedAfterTask') {
        return {...current, failure: {...current.failure, text: prompt}}
      }
      return current
    })
  }, [])
  const handleRetrySessionEstablished = useCallback((session: EstablishedAhpSession, prompt: string) => {
    releaseRecoveryPrompt(prompt, undefined, recoveryInstanceKeyRef.current)
    setHandoff(current => {
      if (current.phase !== 'failedAfterTask' || current.failure.text !== prompt) return current
      return {
        phase: 'routed',
        chat: current.chat,
        taskId: current.taskId,
        establishedSession: session,
        establishedSessionPending: false,
      }
    })
  }, [])
  const externalRouted = currentHandoff.phase === 'routed' && currentHandoff.chat.externallyProvisioned === true
  const announcedExternalSessionRef = useRef<string | undefined>(undefined)
  const externalEstablishedSession = externalRouted ? currentHandoff.establishedSession : undefined
  const externalHandoffInstanceKey = externalRouted ? currentHandoff.chat.instanceKey : undefined
  useEffect(() => {
    if (!externalEstablishedSession || !externalHandoffInstanceKey) return
    if (announcedExternalSessionRef.current === externalHandoffInstanceKey) return
    announcedExternalSessionRef.current = externalHandoffInstanceKey
    announce('Connected.')
  }, [externalEstablishedSession, externalHandoffInstanceKey])
  const sessionContextValue = useMemo(
    () => ({
      instanceKey: 'chat' in currentHandoff ? currentHandoff.chat.instanceKey : undefined,
      chatApiURL: 'chat' in currentHandoff ? currentHandoff.chat.config?.apiURL : undefined,
      launchEnvironmentKind: 'chat' in currentHandoff ? currentHandoff.chat.environmentKind : undefined,
      launchOwnerLogin:
        'chat' in currentHandoff ? provisioningWorkbenchOwner(currentHandoff.chat.startRequest) : undefined,
      externallyProvisioned: 'chat' in currentHandoff ? currentHandoff.chat.externallyProvisioned : undefined,
      establishedSession:
        currentHandoff.phase === 'routed' || currentHandoff.phase === 'failedAfterTask'
          ? currentHandoff.establishedSession
          : undefined,
      establishedSessionPending: currentHandoff.phase === 'routed' ? currentHandoff.establishedSessionPending : false,
      provisioningTaskId,
      provisioningPrompt:
        currentHandoff.phase === 'routed' || currentHandoff.phase === 'failedAfterTask'
          ? currentHandoff.chat.startRequest.prompt
          : undefined,
      initialSelectedSpace:
        'chat' in currentHandoff ? currentHandoff.chat.startRequest.initialSelectedSpace : undefined,
      provisioningDraft:
        // The established composer falls back to this, so a sent prompt must not outlive provisioning.
        currentHandoff.phase === 'provisioning' ||
        (currentHandoff.phase === 'routed' && currentHandoff.establishedSessionPending) ||
        externalRouted
          ? provisioningDraft
          : undefined,
      onProvisioningDraftChange: setProvisioningDraft,
      onProvisionedUserRename:
        currentHandoff.phase === 'routed' || currentHandoff.phase === 'failedAfterTask'
          ? handleProvisionedUserRename
          : undefined,
      submissionFailure:
        currentHandoff.phase === 'failedAfterTask' && !currentHandoff.failureDismissed
          ? currentHandoff.failure
          : undefined,
      restoredPrompt:
        currentHandoff.phase === 'failedAfterTask'
          ? currentHandoff.promptAccepted
            ? undefined
            : currentHandoff.failure.text
          : undefined,
      retryPrompt: currentHandoff.phase === 'failedAfterTask' ? currentHandoff.chat.startRequest.prompt : undefined,
      configureRetrySession:
        currentHandoff.phase === 'failedAfterTask'
          ? configureSessionForMode(currentHandoff.chat.initialSelectedMode)
          : undefined,
      sessionBindingFailed: currentHandoff.phase === 'failedAfterTask' && !currentHandoff.establishedSession,
      onRestoredPromptChange: handleRestoredPromptChange,
      onRestoredPromptAccepted: handleRestoredPromptAccepted,
      onRetrySessionEstablished: handleRetrySessionEstablished,
      clearSubmissionFailure: clearTaskSubmissionFailure,
    }),
    [
      clearTaskSubmissionFailure,
      currentHandoff,
      externalRouted,
      handleProvisionedUserRename,
      handleRestoredPromptChange,
      handleRestoredPromptAccepted,
      handleRetrySessionEstablished,
      provisioningDraft,
      provisioningTaskId,
    ],
  )
  const isProvisionedTaskLocation =
    provisioningTaskId !== undefined && location.pathname === generateTaskPathForRepo({taskId: provisioningTaskId})
  const routedHandoffTaskId = currentHandoff.phase === 'routed' ? currentHandoff.taskId : undefined
  const handoffSessionEstablished = currentHandoff.phase === 'routed' && currentHandoff.establishedSession !== undefined
  useEffect(() => {
    // Routing unmounts the optimistic textarea, so focus drops to <body>.
    // Focus the read-only replacement composer as soon as the task is routed;
    // TaskChat transfers that focus when the established composer is ready.
    if (
      !isProvisionedTaskLocation ||
      !routedHandoffTaskId ||
      focusedProvisionedTaskRef.current === routedHandoffTaskId
    ) {
      return
    }
    const frame = requestAnimationFrame(() => {
      const reclaimable =
        provisionedFallbackFocusRef.current !== undefined &&
        document.activeElement === provisionedFallbackFocusRef.current
      if (document.activeElement !== document.body && !reclaimable) {
        focusedProvisionedTaskRef.current = routedHandoffTaskId
        return
      }
      const composer = document.querySelector<HTMLElement>('react-app textarea:not([disabled])')
      const target = [
        composer,
        document.querySelector<HTMLElement>('react-app h1:not(.sr-only)'),
        document.querySelector<HTMLElement>('react-app [role="region"][aria-label="Task activity"]'),
      ].find(candidate => candidate && candidate.getClientRects().length > 0)
      if (!target) return
      if (target.tagName === 'H1') target.tabIndex = -1
      target.focus()
      // Latch only once focus reached the composer, or once the session is
      // established and no better target is coming.
      if (target === composer || handoffSessionEstablished) {
        provisionedFallbackFocusRef.current = undefined
        focusedProvisionedTaskRef.current = routedHandoffTaskId
      } else {
        provisionedFallbackFocusRef.current = target
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [handoffSessionEstablished, isProvisionedTaskLocation, routedHandoffTaskId])

  const content = (
    <PersistentAgentsChatContext value={actions}>
      <PersistentAgentsChatSessionContext value={sessionContextValue}>
        <PersistentAgentsLiveChatContext value={currentHandoff.phase === 'legacy'}>
          {currentHandoff.phase === 'legacy' && isRuntimeProvisioned(currentHandoff.chat) ? (
            <>
              <LiveAgentsChat
                state={currentHandoff.chat}
                taskId={currentHandoff.taskId}
                onReset={resetLiveChat}
                onSessionProvisioned={handleSessionProvisioned}
              />
              {taskRouteError ? <PersistentAgentsLiveChatQueryErrorReporter error={taskRouteError} /> : null}
            </>
          ) : currentHandoff.phase === 'provisioning' ||
            currentHandoff.phase === 'routed' ||
            currentHandoff.phase === 'failedAfterTask' ? (
            <TaskViewLiveAgentsChat
              key={currentHandoff.chat.instanceKey}
              state={currentHandoff.chat}
              onSessionProvisioned={handleTaskViewSessionProvisioned}
              onSessionEstablished={handleTaskViewSessionEstablished}
              onSessionEstablishmentFailed={handleTaskViewSessionEstablishmentFailed}
              onAbandonedSubmissionSettled={handleAbandonedSubmissionSettled}
              userRenameRef={provisionedUserRenameRef}
            >
              {isProvisionedTaskLocation ? (
                <Outlet />
              ) : (
                <QuickChatProvisioningTaskView
                  state={currentHandoff.chat}
                  draft={provisioningDraft}
                  onDraftChange={setProvisioningDraft}
                  onReset={resetLiveChat}
                />
              )}
            </TaskViewLiveAgentsChat>
          ) : (
            <Outlet />
          )}
        </PersistentAgentsLiveChatContext>
      </PersistentAgentsChatSessionContext>
    </PersistentAgentsChatContext>
  )

  if (isTaskRoute && currentHandoff.phase === 'idle') {
    return content
  }

  return (
    <InstructionsProviderMountBoundary onMount={handleInstructionsProviderMounted}>
      {chatProviderPayload ? (
        <CustomInstructionsProvider
          personalInstructions={chatProviderPayload.personalInstructions}
          customInstructions={chatProviderPayload.customInstructions}
          references={chatProviderPayload.refs}
          instructionScope={'chat' in currentHandoff && currentHandoff.chat.repository ? 'repository' : 'chat'}
          waitForInitialInstructionRules
          loadingFallback={content}
          onInstructionRulesPublished={handleInstructionRulesPublished}
        >
          {content}
        </CustomInstructionsProvider>
      ) : (
        <CustomInstructionsProvider
          instructionScope={'chat' in currentHandoff && currentHandoff.chat.repository ? 'repository' : 'chat'}
          waitForInitialInstructionRules
          loadingFallback={content}
          onInstructionRulesPublished={handleInstructionRulesPublished}
        >
          {content}
        </CustomInstructionsProvider>
      )}
    </InstructionsProviderMountBoundary>
  )
}

function InstructionsProviderMountBoundary({onMount, children}: {onMount: () => void; children: ReactNode}) {
  useLayoutEffect(onMount, [onMount])
  return children
}

function TaskViewLiveAgentsChat({
  state,
  onSessionProvisioned,
  onSessionEstablished,
  onSessionEstablishmentFailed,
  onAbandonedSubmissionSettled,
  userRenameRef,
  children,
}: {
  state: LiveAgentsChatState
  onSessionProvisioned: (instanceKey: string, taskId: string, prompt: string) => void
  onSessionEstablished: (instanceKey: string, session: EstablishedAhpSession, prompt: string) => void
  onSessionEstablishmentFailed: (
    instanceKey: string,
    failure: SubmissionFailure,
    provisionedTaskId: string | undefined,
  ) => void
  onAbandonedSubmissionSettled: (apiURL: string, instanceKey: string, settledPrompt: string | undefined) => void
  userRenameRef: RefObject<(() => void) | undefined>
  children: React.ReactNode
}) {
  useInitializeAuthToken(state.payload.initialAuthToken)
  useSSOChangeDetection(state.payload.initialAuthToken?.ssoOrgIDs)

  return (
    <AppPayloadContext value={state.payload}>
      <CurrentUserProvider currentUser={state.payload.currentUser} currentUserIsSessionOwner>
        <ConsecutiveCAPI401sProvider>
          <ErrorBoundary boundaryName="TaskViewLiveCopilotAgentsChat">
            {isRuntimeProvisioned(state) ? (
              <QuickChatProvisioner
                state={state}
                onSessionProvisioned={onSessionProvisioned}
                onSessionEstablished={onSessionEstablished}
                onSessionEstablishmentFailed={onSessionEstablishmentFailed}
                onAbandonedSubmissionSettled={onAbandonedSubmissionSettled}
                userRenameRef={userRenameRef}
              >
                {children}
              </QuickChatProvisioner>
            ) : (
              children
            )}
          </ErrorBoundary>
        </ConsecutiveCAPI401sProvider>
      </CurrentUserProvider>
    </AppPayloadContext>
  )
}

function QuickChatProvisioningTaskView({
  state,
  draft,
  onDraftChange,
  onReset,
}: {
  state: LiveAgentsChatState
  draft: string
  onDraftChange: (draft: string) => void
  onReset: () => void
}) {
  const {payload, instanceKey} = state
  const task = useMemo(() => optimisticQuickChatTask(state), [state])
  const taskViewRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (document.activeElement !== document.body) return
    taskViewRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus()
  }, [])

  const taskContent = (
    <SidebarAgentFilterProvider>
      {/* taskChatContent only fills inside a full-height parent, as on the routed page. */}
      <div className={styles.taskChatHost}>
        <div ref={taskViewRef} className={styles.taskChatContent}>
          <TaskChat
            task={task}
            sessions={[]}
            currentUser={payload.currentUser}
            currentUserCanPush={false}
            steeringEnabled={false}
            logsPollingInterval={payload.logsPollingInterval}
            sessionsPollingInterval={payload.sessionsPollingInterval}
            initialAuthToken={payload.initialAuthToken}
            useTokenBasedBilling={payload.useTokenBasedBilling}
            agentQuotaSnapshot={payload.agentQuotaSnapshot}
            showHeader
            hideArchive
            hideActions
            hideEnvironmentVisual
            navigateOnDelete={false}
            establishedAhpSessionPending
            requiresEstablishedAhpBootstrap
            provisioningPrompt={state.startRequest.prompt}
            provisionalLaunchKind={state.environmentKind}
            provisioningDraft={draft}
            onProvisioningDraftChange={onDraftChange}
            chatApiUrl={state.config?.apiURL}
            profileSlugOverride={provisionalAgentSlug(state)}
          />
        </div>
      </div>
    </SidebarAgentFilterProvider>
  )
  const agentsWorkbenchEnabled =
    copilotFeatureFlags.quickChatTaskViewEnabled && state.environmentKind === 'managed-sandbox'

  return (
    <AgentsSidebarLayout
      agentsEnabled={payload.agentsEnabled !== false}
      showSpacesNav={payload.spacesEnabled}
      showCustomizeNav={payload.customizeEnabled}
      currentUserLogin={payload.currentUser?.login}
      currentUserId={payload.currentUser?.id}
      enterpriseSandboxEnabled={payload.enterpriseSandboxEnabled !== false}
      taskGroups={payload.taskGroups}
      contentKey={instanceKey}
      chatApiURL={state.config?.apiURL}
      sidebarProps={{onNavigateHome: onReset, navigateToAgentsPage: true}}
    >
      <SidebarPageLayout.Content as="div" padding="none" width="full">
        {!isFeatureEnabled('copilot_mission_control_sticky_header') && <SidebarPageLayout.SidebarTrigger />}
        {agentsWorkbenchEnabled ? (
          <ErrorBoundary boundaryName="AgentsWorkbenchHost" fallback={taskContent}>
            <Suspense fallback={<AgentsWorkbenchLoadingState />}>
              <DynamicAgentsWorkbenchHost
                conversationScope={{surface: 'chat', id: instanceKey}}
                environmentKind={state.environmentKind}
                ownerLogin={provisioningWorkbenchOwner(state.startRequest)}
                previewUrl={state.payload.previewUrl ?? ''}
              >
                {taskContent}
              </DynamicAgentsWorkbenchHost>
            </Suspense>
          </ErrorBoundary>
        ) : (
          taskContent
        )}
      </SidebarPageLayout.Content>
    </AgentsSidebarLayout>
  )
}

export function PersistentAgentsTaskRouteErrorBoundary() {
  const hasLiveChat = use(PersistentAgentsLiveChatContext)
  return hasLiveChat ? (
    <PersistentAgentsLiveChatRouteErrorBoundary />
  ) : (
    <RootAppRouteErrorElement appName="copilot-agents" />
  )
}

function PersistentAgentsLiveChatRouteErrorBoundary() {
  const routeError = useRouteError()
  return <PersistentAgentsLiveChatQueryErrorReporter error={routeError} />
}

function PersistentAgentsLiveChatQueryErrorReporter({error}: {error: unknown}) {
  useReportRouteError(error, {
    reactAppName: 'copilot-agents',
    boundaryName: 'copilotAgentsTaskRoute',
  })
  return null
}

function QuickChatProvisioner({
  state,
  onSessionProvisioned,
  onSessionEstablished,
  onSessionEstablishmentFailed,
  onAbandonedSubmissionSettled,
  userRenameRef,
  children,
}: {
  state: RuntimeLiveAgentsChatState
  onSessionProvisioned: (instanceKey: string, taskId: string, prompt: string) => void
  onSessionEstablished: (instanceKey: string, session: EstablishedAhpSession, prompt: string) => void
  onSessionEstablishmentFailed: (
    instanceKey: string,
    failure: SubmissionFailure,
    provisionedTaskId: string | undefined,
  ) => void
  onAbandonedSubmissionSettled: (apiURL: string, instanceKey: string, settledPrompt: string | undefined) => void
  userRenameRef: RefObject<(() => void) | undefined>
  children: React.ReactNode
}) {
  const submittedInstanceRef = useRef<string | undefined>(undefined)
  const provisionedTaskIdRef = useRef<string | undefined>(undefined)
  const config = useMemo(() => ({...state.config, instanceKey: state.instanceKey}), [state.config, state.instanceKey])
  const {
    initialSubmission,
    onSessionEstablished: retainSession,
    onUserRename,
  } = useMissionControlRuntime(config, {
    onTaskCreated: taskId => {
      provisionedTaskIdRef.current = taskId
      onSessionProvisioned(state.instanceKey, taskId, state.startRequest.prompt.text)
    },
    onSessionProvisioned: identity => {
      if (identity.taskId) {
        provisionedTaskIdRef.current = identity.taskId
        onSessionProvisioned(state.instanceKey, identity.taskId, state.startRequest.prompt.text)
      }
    },
  })

  useEffect(() => {
    userRenameRef.current = onUserRename
    return () => {
      userRenameRef.current = undefined
    }
  }, [onUserRename, userRenameRef])
  const handleSubmissionFailure = useEffectEvent((error: unknown) => {
    const message = userFacingSubmissionMessage(error) ?? SUBMIT_ERROR_MESSAGE
    const provisionedTaskId = provisionedTaskIdRef.current
    recordActivationTraceId(provisionedTaskId ?? state.instanceKey, activationTraceIdFromError(error))
    const failedAfterTask = provisionedTaskId !== undefined
    sendEvent('agent_sessions.task_actions', {
      target: 'QUICK_CHAT_PROVISION_FAILED',
      task_id: provisionedTaskId,
      phase: failedAfterTask ? 'failed_after_task' : 'failed_before_task',
      instance_key: state.instanceKey,
      ...provisioningErrorMetadata(error),
    })
    onSessionEstablishmentFailed(state.instanceKey, {message, text: state.startRequest.prompt.text}, provisionedTaskId)
    announce(`${failedAfterTask ? 'Chat message could not be sent.' : 'Chat could not be created.'} ${message}`, {
      assertive: true,
    })
  })

  useEffect(() => {
    if (!initialSubmission || submittedInstanceRef.current === state.instanceKey) return
    let cancelled = false
    // Names the launch submission so the task view can open the artifact its first
    // turn produces. Provisioning resolves the task id before the first dispatch,
    // so it is available by the time this runs.
    let armedTaskId: string | undefined
    let armedSubmissionId: number | undefined
    const submit = async () => {
      try {
        const configureSession = configureSessionForMode(state.initialSelectedMode)
        const session = await initialSubmission.submit({
          prompt: state.startRequest.prompt,
          origin: 'new',
          ...(state.startRequest.metadata ? {metadata: state.startRequest.metadata} : {}),
          ...(state.initialSelectedMode ? {mode: state.initialSelectedMode} : {}),
          ...(configureSession ? {configureSession} : {}),
          beforeDispatch: async () => {
            const taskId = provisionedTaskIdRef.current
            if (!taskId) return
            armedTaskId = taskId
            armedSubmissionId = markUserStartedTurn(taskId)
          },
          afterDispatch: receipt => {
            if (armedTaskId && armedSubmissionId !== undefined) {
              bindUserStartedTurn(armedTaskId, armedSubmissionId, receipt)
            }
          },
        })
        if (cancelled) {
          onAbandonedSubmissionSettled(state.config.apiURL, state.instanceKey, state.startRequest.prompt.text)
          return
        }
        retainSession(session)
        onSessionEstablished(state.instanceKey, session, state.startRequest.prompt.text)
      } catch (error) {
        // A failed launch retires the submission even when the dispatch already named
        // a turn, because opening an artifact behind the failure would contradict it.
        // A submission that was abandoned rather than failed keeps its mark above.
        if (armedTaskId && armedSubmissionId !== undefined) unmarkUserStartedTurn(armedTaskId, armedSubmissionId)
        if (cancelled) {
          onAbandonedSubmissionSettled(state.config.apiURL, state.instanceKey, undefined)
          return
        }
        handleSubmissionFailure(error)
      }
    }
    // Deferred a tick so StrictMode's dev remount cancels this setup before it
    // fires. Submitting during the effect body instead lets the discarded mount
    // reset the runtime bootstrap and strand the surviving one.
    const timer = setTimeout(() => {
      if (submittedInstanceRef.current === state.instanceKey) return
      submittedInstanceRef.current = state.instanceKey
      void submit()
    }, 0)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [
    initialSubmission,
    onSessionEstablished,
    onSessionEstablishmentFailed,
    onAbandonedSubmissionSettled,
    retainSession,
    state.config.apiURL,
    state.startRequest,
    state.initialSelectedMode,
    state.instanceKey,
  ])

  return children
}

function LiveAgentsChat({
  state,
  taskId,
  onReset,
  onSessionProvisioned,
}: {
  state: RuntimeLiveAgentsChatState
  taskId?: string
  onReset: () => void
  onSessionProvisioned: (identity: {taskId?: string}) => void
}) {
  const {payload, startRequest, initialSelectedMode, instanceKey} = state
  const config = useMemo(() => ({...state.config, instanceKey}), [instanceKey, state.config])
  useInitializeAuthToken(payload.initialAuthToken)
  useSSOChangeDetection(payload.initialAuthToken?.ssoOrgIDs)

  return (
    <AppPayloadContext value={payload}>
      <CurrentUserProvider currentUser={payload.currentUser} currentUserIsSessionOwner>
        <ConsecutiveCAPI401sProvider>
          <ErrorBoundary boundaryName="LiveCopilotAgentsChat">
            <AgentsSidebarLayout
              agentsEnabled={payload.agentsEnabled !== false}
              showSpacesNav={payload.spacesEnabled}
              showCustomizeNav={payload.customizeEnabled}
              currentUserLogin={payload.currentUser?.login}
              currentUserId={payload.currentUser?.id}
              enterpriseSandboxEnabled={payload.enterpriseSandboxEnabled !== false}
              taskGroups={payload.taskGroups}
              contentKey={instanceKey}
              chatApiURL={config.apiURL}
              activeTaskId={taskId}
              sidebarProps={{onNavigateHome: onReset, navigateToAgentsPage: true, activeTaskId: taskId}}
            >
              {/* as="div" avoids a duplicate <main>; the Rails layout already wraps this root in <main>. */}
              <SidebarPageLayout.Content as="div">
                <SidebarPageLayout.SidebarTrigger />
                <ChatAutoProvisionRoute
                  config={config}
                  initialPrompt={startRequest.prompt}
                  initialSubmissionMetadata={startRequest.metadata}
                  initialSelectedSpace={startRequest.initialSelectedSpace}
                  initialSelectedMode={initialSelectedMode}
                  onSessionProvisioned={onSessionProvisioned}
                  deletionDestination={getAgentsPath()}
                />
              </SidebarPageLayout.Content>
            </AgentsSidebarLayout>
          </ErrorBoundary>
        </ConsecutiveCAPI401sProvider>
      </CurrentUserProvider>
    </AppPayloadContext>
  )
}
